import type { Migration, MigrationResult, VersionedJson } from '@builder4/shared/migrations/types';

export function getCurrentVersion<T extends VersionedJson>(data: T): number | null {
  const version = data.version;

  if (version === undefined || version === null) {
    return 0;
  }

  if (typeof version !== 'number' || !Number.isInteger(version) || version < 0) {
    return null;
  }

  return version;
}

export function setVersion<T extends VersionedJson>(data: T, version: number): T {
  const copy = Object.assign(Object.create(Object.getPrototypeOf(data)), data);
  copy.version = version;
  return copy;
}

/**
 * Runs all necessary migrations to bring VersionedJson to the current schema version.
 *
 * @param data - The JSON to migrate
 * @returns Migration result with migrated JSON and metadata
 */
export function runMigrations<T extends VersionedJson>(
  data: T,
  migrations: Migration<T>[],
  targetVersion: number,
): MigrationResult<T> {
  const originalVersion = getCurrentVersion(data);

  if (originalVersion === null) {
    throw new Error('Cannot run migrations: VersionedJson has an invalid version');
  }

  // No migrations needed if already at or above target version and version is explicitly set
  if (originalVersion >= targetVersion) {
    return {
      json: data,
      originalVersion,
      finalVersion: originalVersion,
      migrationsApplied: false,
      migrationsCount: 0,
      error: false,
    };
  }

  const applicableMigrations = migrations
    .filter((m) => m.version > originalVersion && m.version <= targetVersion)
    .sort((a, b) => a.version - b.version);

  // Create a shallow copy that preserves the prototype chain (for class instance methods)
  let migratedJson = Object.assign(Object.create(Object.getPrototypeOf(data)), data);
  let currentVersion = originalVersion;

  try {
    for (const migration of applicableMigrations) {
      migratedJson = migration.migrate(migratedJson);
      migratedJson = setVersion(migratedJson, migration.version);
      currentVersion = migration.version;
    }

    return {
      json: migratedJson,
      originalVersion,
      finalVersion: targetVersion,
      migrationsApplied: true,
      migrationsCount: applicableMigrations.length,
      error: false,
    };
  } catch (error) {
    return {
      json: migratedJson,
      originalVersion,
      finalVersion: currentVersion,
      migrationsApplied: true,
      migrationsCount: applicableMigrations.findIndex((m) => m.version > currentVersion),
      error: true,
      errorMessage: error instanceof Error ? error.message : String(error),
    };
  }
}
